import EvaluationFlow from "../../../flow/EvaluationFlow"
import { InAppMessageEligibilityRequest } from "./InAppMessageEligibilityRequest"
import { InAppMessageEligibilityEvaluation } from "./InAppMessageEligibilityEvaluation"
import FlowEvaluator from "../../../flow/FlowEvaluator"
import { EvaluatorContext } from "../../../evalautor/Evaluator"
import { DecisionReason } from "../../../../model/model"
import { InAppMessageFrequencyCapMatcher, InAppMessageMatcher } from "../../../target/InAppMessageMatcher"
import { InAppMessageLayoutRequest } from "../layout/InAppMessageLayoutRequest"
import { InAppMessageLayoutEvaluator } from "../layout/InAppMessageLayoutEvaluator"
import { InAppMessageLayoutEvaluation } from "../layout/InAppMessageLayoutEvaluation"

export type InAppMessageEligibilityFlow = EvaluationFlow<
  InAppMessageEligibilityRequest,
  InAppMessageEligibilityEvaluation
>

export default interface InAppMessageEligibilityFlowEvaluator
  extends FlowEvaluator<InAppMessageEligibilityRequest, InAppMessageEligibilityEvaluation> {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined
}

export class PlatformInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isWebPlatform = request.inAppMessage.supports("WEB")
    if (!isWebPlatform) {
      return InAppMessageEligibilityEvaluation.ineligible(request, context, DecisionReason.UNSUPPORTED_PLATFORM)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class OverrideInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  constructor(private readonly userOverrideMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isOverrideMatched = this.userOverrideMatcher.matches(request, context)
    if (isOverrideMatched) {
      return InAppMessageEligibilityEvaluation.eligible(request, context, DecisionReason.OVERRIDDEN)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class DraftInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isDraft = request.inAppMessage.status === "DRAFT"
    if (isDraft) {
      return InAppMessageEligibilityEvaluation.ineligible(request, context, DecisionReason.IN_APP_MESSAGE_DRAFT)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class PauseInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isPause = request.inAppMessage.status === "PAUSE"
    if (isPause) {
      return InAppMessageEligibilityEvaluation.ineligible(request, context, DecisionReason.IN_APP_MESSAGE_PAUSED)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class PeriodInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isWithinPeriod = request.inAppMessage.period.within(request.timestamp)
    if (!isWithinPeriod) {
      return InAppMessageEligibilityEvaluation.ineligible(request, context, DecisionReason.NOT_IN_IN_APP_MESSAGE_PERIOD)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class TimetableInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isWithinTimetable = request.inAppMessage.timetable.within(request.timestamp)
    if (!isWithinTimetable) {
      return InAppMessageEligibilityEvaluation.ineligible(
        request,
        context,
        DecisionReason.NOT_IN_IN_APP_MESSAGE_TIMETABLE
      )
    }
    return nextFlow.evaluate(request, context)
  }
}

export class HiddenInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  constructor(private readonly hiddenMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isHidden = this.hiddenMatcher.matches(request, context)
    if (isHidden) {
      return InAppMessageEligibilityEvaluation.ineligible(request, context, DecisionReason.IN_APP_MESSAGE_HIDDEN)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class TargetInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  constructor(private readonly targetMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isTargetMatched = this.targetMatcher.matches(request, context)
    if (!isTargetMatched) {
      return InAppMessageEligibilityEvaluation.ineligible(request, context, DecisionReason.NOT_IN_IN_APP_MESSAGE_TARGET)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class FrequencyInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  constructor(private readonly frequencyCapMatcher: InAppMessageFrequencyCapMatcher) {}

  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const isFrequencyCapped = this.frequencyCapMatcher.matches(request, context)
    if (isFrequencyCapped) {
      return InAppMessageEligibilityEvaluation.ineligible(
        request,
        context,
        DecisionReason.IN_APP_MESSAGE_FREQUENCY_CAPPED
      )
    }

    return nextFlow.evaluate(request, context)
  }
}

export class EligibleInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation {
    return InAppMessageEligibilityEvaluation.eligible(request, context, DecisionReason.IN_APP_MESSAGE_TARGET)
  }
}

export class LayoutResolveInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityFlowEvaluator {
  constructor(private readonly layoutEvaluator: InAppMessageLayoutEvaluator) {}

  evaluate(
    request: InAppMessageEligibilityRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityFlow
  ): InAppMessageEligibilityEvaluation | undefined {
    const layoutRequest = InAppMessageLayoutRequest.of(request)
    const layoutEvaluation = this.layoutEvaluator.evaluate(layoutRequest, EvaluatorContext.create())
    context.set(InAppMessageLayoutEvaluation, layoutEvaluation)

    return nextFlow.evaluate(request, context)
  }
}
